import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import { toEnum } from "../../../lib/onboarding/onboardingVocabulary";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./FertilityJourneyTypesQuestion.module.css";

interface FertilityJourneyTypesQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
	showValidation?: boolean;
	excludedJourneyTypes?: string[];
}

// Canonical option values currently offered by this UI. Legacy values that
// were removed from the option list (e.g. "Reciprocal IVF", "Trying naturally")
// can still sit on user records from old onboarding flows; we drop them when
// loading so they don't ride along on every save. Single-select for now —
// older records may carry 2-3 entries, which we collapse to the first valid.
const VALID_JOURNEY_TYPE_VALUES = new Set([
	"IUI",
	"IVF",
	"Surrogacy",
	"Egg donation",
	"Sperm donation",
	"Egg freezing",
	"I don't know yet",
	"Other",
]);

function sanitizeSelections(values: string[]): string[] {
	const firstValid = values.find(
		(v) => VALID_JOURNEY_TYPE_VALUES.has(v) || v.startsWith("Other - "),
	);
	return firstValid ? [firstValid] : [];
}

export function FertilityJourneyTypesQuestion({
	answers,
	onInputChange,
	showValidation = false,
	excludedJourneyTypes,
}: FertilityJourneyTypesQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const groupId = useId();
	const hasInitialized = useRef(false);
	// Fire the "Other" text event once per visit, not per keystroke.
	const otherTracked = useRef(false);

	const currentSelections = Array.isArray(answers.fertilityJourneyTypes)
		? sanitizeSelections(answers.fertilityJourneyTypes)
		: [];

	// Extract existing "Other - ..." text if present
	const existingOtherEntry = currentSelections.find((v) =>
		v.startsWith("Other - "),
	);
	const initialOtherText = existingOtherEntry
		? existingOtherEntry.replace("Other - ", "")
		: "";

	const [otherText, setOtherText] = useState(initialOtherText);

	// Initialize otherText from existing data on mount
	useEffect(() => {
		if (!hasInitialized.current && initialOtherText) {
			setOtherText(initialOtherText);
			hasInitialized.current = true;
		}
	}, [initialOtherText]);

	// Self-heal: if the loaded answer carries legacy/orphan values that the
	// current UI no longer renders, push the sanitized list back so a
	// no-change save doesn't re-persist orphans.
	useEffect(() => {
		const raw = answers.fertilityJourneyTypes;
		if (!Array.isArray(raw)) return;
		const cleaned = sanitizeSelections(raw);
		if (cleaned.length !== raw.length) {
			onInputChange("fertilityJourneyTypes", cleaned);
		}
	}, [answers.fertilityJourneyTypes, onInputChange]);

	const handleSelectOption = (value: string) => {
		// Radio semantics (Figma V5_Paths): selecting always replaces; there is
		// no deselect. A repeated tap (incl. mobile ghost double-taps) must keep
		// the selection — a transient [] here silently dropped re-selections.
		const newSelections: string[] = [value];

		if (value !== "Other") {
			setOtherText("");
		}

		onInputChange("fertilityJourneyTypes", newSelections);

		// Single-select (radio semantics) — one value replaces the prior.
		logOnboarding("journey_type_selected", {
			field_name: "fertilityJourneyTypes",
			selected_value: value,
		});
	};

	const handleOtherTextChange = (text: string) => {
		setOtherText(text);

		const newValue = text.trim() ? `Other - ${text.trim()}` : "Other";
		const newSelections = [newValue];

		onInputChange("fertilityJourneyTypes", newSelections);

		if (text.trim() && !otherTracked.current) {
			otherTracked.current = true;
			logOnboarding("journey_type_other_text_entered", {
				field_name: "fertilityJourneyTypes",
			});
		}
	};

	const hasError = showValidation && currentSelections.length === 0;

	const journeyTypeOptions = [
		{ value: "IUI", label: t("questions.fertilityPath.pathOptions.iui") },
		{ value: "IVF", label: t("questions.fertilityPath.pathOptions.ivf") },
		{
			value: "Surrogacy",
			label: t("questions.fertilityPath.pathOptions.surrogacy"),
		},
		{
			value: "Egg donation",
			label: t("questions.fertilityPath.pathOptions.eggDonation"),
		},
		{
			value: "Sperm donation",
			label: t("questions.fertilityPath.pathOptions.spermDonation"),
		},
		{
			value: "Egg freezing",
			label: t("questions.fertilityPath.pathOptions.eggFreezing"),
		},
		{
			value: "I don't know yet",
			label: t("questions.fertilityPath.dontKnow"),
		},
		{ value: "Other", label: t("questions.fertilityPath.other") },
	];

	const excludedSet = new Set(excludedJourneyTypes ?? []);
	const visibleJourneyOptions = journeyTypeOptions.filter((option) => {
		const asEnum = toEnum("journeyType", option.value);
		return !asEnum || !excludedSet.has(asEnum);
	});

	return (
		<div className={styles.container} dir={direction}>
			<fieldset className={styles.optionsList} id={groupId}>
				{visibleJourneyOptions.map((option) => {
					const isSelected =
						option.value === "Other"
							? currentSelections.includes("Other") ||
								currentSelections.some((v) => v.startsWith("Other - "))
							: currentSelections.includes(option.value);

					return (
						<label
							key={option.value}
							className={`${styles.optionCard} ${
								isSelected ? styles.optionCardSelected : ""
							} ${hasError ? styles.optionCardError : ""}`}
						>
							<input
								type="radio"
								name={groupId}
								checked={isSelected}
								onChange={() => handleSelectOption(option.value)}
								className={styles.hiddenCheckbox}
								aria-label={option.label}
							/>
							<div
								className={`${styles.radio} ${
									isSelected ? styles.radioSelected : ""
								}`}
							>
								{isSelected && <span className={styles.radioDot} />}
							</div>
							<span className={styles.optionLabel}>{option.label}</span>
						</label>
					);
				})}
			</fieldset>
			{(currentSelections.includes("Other") ||
				currentSelections.some((v) => v.startsWith("Other - "))) && (
				<div className={styles.otherInputWrapper}>
					<input
						type="text"
						value={otherText}
						onChange={(e) => handleOtherTextChange(e.target.value)}
						placeholder={t("questions.fertilityPath.otherJourneyPlaceholder")}
						className={styles.otherInput}
						aria-label={t("questions.fertilityPath.otherJourneyPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
